import { useState, useEffect, useRef } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { IoIosSearch } from "react-icons/io";
import LoaderOverlay from "../Loader/LoaderOverlay";
import SeparationLine from "../SeparationLine/Separation";

interface Option {
    value: string;
    label: string;
}

interface MultiSelectDropdownProps {
    options: Option[];
    placeholder?: string;
    onChange?: (selectedValues: string[], hasChanged: boolean) => void;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    isDropDownLoading: boolean;
    isMobile: boolean;
}

export default function MultiSelectDropdown({
    options,
    placeholder = "Customers",
    onChange,
    isOpen,
    setIsOpen,
    isDropDownLoading,
    isMobile
}: MultiSelectDropdownProps) {
    const STORAGE_KEY = "multiSelectSelectedValues";
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const previousValuesRef = useRef<string[]>([]);

    useEffect(() => {
        const storedValues = localStorage.getItem(STORAGE_KEY);
        if (storedValues) {
            const parsedValues = JSON.parse(storedValues);
            setSelectedValues(parsedValues);
            previousValuesRef.current = parsedValues;
            if (onChange) onChange(parsedValues, false);
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                const hasChanged = !areArraysEqual(previousValuesRef.current, selectedValues);
                setIsOpen(false);
                setSearchTerm("")
                if (onChange) onChange(selectedValues, hasChanged);
                previousValuesRef.current = selectedValues;
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, selectedValues]);

    const toggleDropdown = () => {
        if (isOpen) {
            setSearchTerm("")
            const hasChanged = !areArraysEqual(previousValuesRef.current, selectedValues);
            if (onChange) onChange(selectedValues, hasChanged);
            previousValuesRef.current = selectedValues;
        }
        setIsOpen(!isOpen);
    };

    const toggleSelection = (value: string) => {
        let newValues;
        if (selectedValues.includes(value)) {
            newValues = selectedValues.filter((v) => v !== value);
        } else {
            newValues = [...selectedValues, value];
        }
        setSelectedValues(newValues);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newValues));
    };

    const clearAll = () => {
        const hasChanged = selectedValues.length > 0;
        setSelectedValues([]);
        localStorage.removeItem(STORAGE_KEY);
        if (onChange) onChange([], hasChanged);
    };

    const areArraysEqual = (arr1: string[], arr2: string[]) => {
        if (arr1.length !== arr2.length) return false;
        return arr1.sort().join(",") === arr2.sort().join(",");
    };

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div
            className="relative w-72"
            ref={dropdownRef}
            style={{ width: isMobile ? "100%" : "" }}
        >
            <button
                className="w-full border border-darkcolor-400 rounded-lg p-1 flex justify-between items-center bg-darkcolor-700"
                onClick={toggleDropdown}
                style={{ width: isMobile ? "full" : "225px" }}
            >
                <span className="text-gray-700 flex items-center gap-2 pl-2">
                    {placeholder}
                    {selectedValues.length > 0 && (
                        <span className="rounded-sm max-h-6 p-1 px-2 bg-primary-300 text-primary-700 text-sm font-medium leading-4">
                            {selectedValues.length}
                        </span>
                    )}
                </span>
                {isOpen ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {isOpen && (
                <div className="absolute mt-2 w-full bg-darkcolor-700 border border-darkcolor-400 rounded-lg shadow-lg py-2 px-1 z-10">

                    {/* Search and Selected Values Section */}
                    <div className="p-2" style={{ backgroundColor: "#4A5463" }}>
                        {/* Search Input */}
                        <div className="flex items-center w-full bg-darkcolor-700 border border-darkcolor-400 rounded mb-2 px-2">
                            <IoIosSearch className="text-gray-400 text-xl" />
                            <input
                                type="text"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full h-8 p-1 bg-darkcolor-700 text-white focus:outline-none"
                            />
                        </div>

                        {/* Show loader while dropdown is loading */}
                        {isDropDownLoading ? (
                            <div className="flex justify-center items-center py-4 text-white">
                                Loading...
                            </div>
                        ) : (
                            <>
                                {/* Selected Values */}
                                {selectedValues.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-2 max-h-[140px] overflow-y-auto rounded text-sm">
                                        {selectedValues.map((value) => {
                                            const option = options.find((opt) => opt.value === value);
                                            return (
                                                <div
                                                    key={value}
                                                    className="border border-darkcolor-200 bg-darkcolor-600 px-2 py-1 rounded flex items-center text-xs"
                                                >
                                                    {option?.label}
                                                    <button onClick={() => toggleSelection(value)} className="ml-2 text-red-500">
                                                        X
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {/* Clear All Button */}
                                {selectedValues.length > 0 && (
                                    <button
                                        onClick={clearAll}
                                        className="text-sm ml-auto block"
                                        style={{ color: '#3b82f6' }} // Inline style to ensure blue color
                                    >
                                        Clear all
                                    </button>
                                )}

                            </>
                        )}
                    </div>
                    <SeparationLine />

                    {/* Checkbox Options Section */}
                    <div className="bg-darkcolor-700 pt-2 max-h-40 overflow-y-auto text-xs">
                        {/* Dropdown Options */}
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <label key={option.value} className="flex items-center gap-2 cursor-pointer p-1">
                                    {/* Custom checkbox */}
                                    <input
                                        type="checkbox"
                                        checked={selectedValues.includes(option.value)}
                                        onChange={() => toggleSelection(option.value)}
                                        className="hidden" // Hide default checkbox
                                    />
                                    <span className="w-4 h-4 flex items-center justify-center border border-darkcolor-500 rounded-sm bg-transparent mr-2 relative">
                                        {/* Custom tick mark */}
                                        {selectedValues.includes(option.value) && (
                                            <span className="absolute text-white text-lg">
                                                ✓
                                            </span>
                                        )}
                                    </span>
                                    {option.label}
                                </label>
                            ))
                        ) : (
                            <p className="text-gray-400 text-sm p-2">No results found</p>
                        )}
                    </div>

                </div>
            )}

        </div>
    );
}